/**
 * RFID Bridge Service
 *
 * Servicio singleton que maneja la conexión WebSocket con el RFID Bridge (app Electron).
 * El bridge corre localmente y comunica con lectoras RFID USB vía PC/SC.
 *
 * Estados:
 * - disconnected: Sin conexión al bridge
 * - connecting: Intentando conectar
 * - connected: Conectado, esperando lectora
 * - reader_ready: Lectora detectada, esperando tarjeta
 * - card_present: Tarjeta detectada, UID disponible
 * - error: Error de conexión
 */

export type BridgeStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reader_ready'
  | 'card_present'
  | 'error';

// Mensajes que recibimos del Bridge
export interface BridgeMessage {
  type: 'READER_CONNECTED' | 'READER_DISCONNECTED' | 'CARD_DETECTED' | 'CARD_REMOVED' | 'WRITE_SUCCESS' | 'ERROR';
  payload?: {
    readerName?: string;
    cardUid?: string;
    cardType?: string;
    error?: string;
  };
}

// Comandos que enviamos al Bridge
export interface WebCommand {
  type: 'READ_CARD' | 'WRITE_CARD' | 'GET_STATUS';
  payload?: {
    data?: string;
    sector?: number;
  };
}

// Eventos que emitimos a los listeners
export type BridgeEvent =
  | { type: 'status_change'; status: BridgeStatus; error?: string }
  | { type: 'card_detected'; uid: string; cardType?: string }
  | { type: 'card_removed' }
  | { type: 'reader_connected'; readerName: string }
  | { type: 'reader_disconnected' };

type BridgeEventListener = (event: BridgeEvent) => void;

const BRIDGE_URL = 'ws://localhost:8765';
const RECONNECT_DELAY = 3000;

class RfidBridgeService {
  private ws: WebSocket | null = null;
  private _status: BridgeStatus = 'disconnected';
  private listeners: Set<BridgeEventListener> = new Set();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shouldReconnect = false;

  currentCardUid: string | null = null;
  currentReaderName: string | null = null;
  lastError: string | null = null;

  get status(): BridgeStatus {
    return this._status;
  }

  get isConnected(): boolean {
    return this._status !== 'disconnected' && this._status !== 'error' && this._status !== 'connecting';
  }

  /**
   * Inicia conexión con el RFID Bridge
   */
  connect(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return; // Ya conectado
    }

    this.shouldReconnect = true;
    this.attemptConnection();
  }

  /**
   * Desconecta del bridge
   */
  disconnect(): void {
    this.shouldReconnect = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.setStatus('disconnected');
    this.currentCardUid = null;
    this.currentReaderName = null;
  }

  /**
   * Suscribe un listener a eventos del bridge
   */
  subscribe(listener: BridgeEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Envía comando al bridge
   */
  sendCommand(command: WebCommand): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(command));
    }
  }

  /**
   * Solicita estado actual al bridge
   */
  requestStatus(): void {
    this.sendCommand({ type: 'GET_STATUS' });
  }

  // --- Métodos privados ---

  private attemptConnection(): void {
    this.setStatus('connecting');
    this.lastError = null;

    try {
      this.ws = new WebSocket(BRIDGE_URL);

      this.ws.onopen = () => {
        this.setStatus('connected');
        this.requestStatus();
      };

      this.ws.onmessage = (event) => {
        try {
          const msg: BridgeMessage = JSON.parse(event.data);
          this.handleMessage(msg);
        } catch (e) {
          console.error('[RfidBridge] Error parsing message:', e);
        }
      };

      this.ws.onerror = () => {
        this.lastError = 'No se pudo conectar al RFID Bridge. Asegúrese de que esté ejecutándose.';
        this.setStatus('error');
      };

      this.ws.onclose = () => {
        this.ws = null;

        if (this.shouldReconnect && this._status !== 'error') {
          this.setStatus('disconnected');
          this.scheduleReconnect();
        }
      };
    } catch (e) {
      this.lastError = 'Error al intentar conectar con el RFID Bridge';
      this.setStatus('error');
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldReconnect) {
        this.attemptConnection();
      }
    }, RECONNECT_DELAY);
  }

  private handleMessage(msg: BridgeMessage): void {
    switch (msg.type) {
      case 'READER_CONNECTED':
        this.currentReaderName = msg.payload?.readerName || 'Unknown Reader';
        this.setStatus('reader_ready');
        this.emit({ type: 'reader_connected', readerName: this.currentReaderName });
        break;

      case 'READER_DISCONNECTED':
        this.currentReaderName = null;
        this.currentCardUid = null;
        this.setStatus('connected');
        this.emit({ type: 'reader_disconnected' });
        break;

      case 'CARD_DETECTED':
        this.currentCardUid = msg.payload?.cardUid || null;
        this.setStatus('card_present');
        if (this.currentCardUid) {
          this.emit({
            type: 'card_detected',
            uid: this.currentCardUid,
            cardType: msg.payload?.cardType
          });
        }
        break;

      case 'CARD_REMOVED':
        this.currentCardUid = null;
        this.setStatus('reader_ready');
        this.emit({ type: 'card_removed' });
        break;

      case 'ERROR':
        this.lastError = msg.payload?.error || 'Error desconocido';
        this.emit({ type: 'status_change', status: this._status, error: this.lastError });
        break;
    }
  }

  private setStatus(status: BridgeStatus): void {
    if (this._status !== status) {
      this._status = status;
      this.emit({ type: 'status_change', status, error: this.lastError || undefined });
    }
  }

  private emit(event: BridgeEvent): void {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (e) {
        console.error('[RfidBridge] Error in listener:', e);
      }
    });
  }
}

// Singleton instance
export const rfidBridge = new RfidBridgeService();
